if (!customElements.get('free-gift-selector')) {
  customElements.define(
    'free-gift-selector',
    class FreeGiftSelector extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.selectGift.bind(this));
        const slider = this.querySelector('.free-gift__option_slider');
        if (slider && !slider.swiper) {
          new Swiper(slider, {
            slidesPerView: 1.2,
            spaceBetween: 10,
            loop: false,
            observer: true,
            observeParents: true,
            breakpoints: { 750: { slidesPerView: 2 } },
          });
        }
      }

      disconnectedCallback() {
        this.querySelector('.free-gift__option_slider')?.swiper?.destroy(true, true);
      }

      async selectGift(event) {
        const button = event.target.closest('[data-variant-id]');
        if (!button || button.disabled || button.getAttribute('aria-pressed') === 'true') return;

        this.setAttribute('aria-busy', 'true');
        this.querySelector('[role="alert"]').hidden = true;

        try {
          const root = window.Shopify?.routes?.root || '/';
          const cartResponse = await fetch(`${root}cart.js`);
          if (!cartResponse.ok) throw new Error('Could not read the cart');

          const cart = await cartResponse.json();
          const currentGifts = cart.items.filter((item) => item.properties?._free_gift === 'true');

          for (const gift of currentGifts) {
            const response = await fetch(`${root}cart/change.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: gift.key, quantity: 0 }),
            });
            if (!response.ok) throw new Error('Could not remove the previous gift');
          }

          const addResponse = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  id: Number(button.dataset.variantId),
                  quantity: 1,
                  properties: { _free_gift: 'true' },
                },
              ],
            }),
          });
          if (!addResponse.ok) throw new Error('Could not add the selected gift');

          await this.refreshCart();
        } catch (error) {
          this.querySelector('[role="alert"]').hidden = false;
          console.error('Free gift selector:', error);
        } finally {
          this.removeAttribute('aria-busy');
        }
      }

      async refreshCart() {
        const sectionIds = ['cart-icon-bubble'];
        if (document.querySelector('#CartDrawer')) sectionIds.push('cart-drawer');
        if (document.querySelector('#main-cart-items')) sectionIds.push('main-cart-items');
        if (document.querySelector('#main-cart-footer')) sectionIds.push('main-cart-footer');

        const url = new URL(window.location.href);
        url.searchParams.set('sections', sectionIds.join(','));
        const response = await fetch(url);
        if (!response.ok) throw new Error('Could not refresh the cart');

        const sections = await response.json();
        this.replaceSection(sections['cart-icon-bubble'], '#shopify-section-cart-icon-bubble');
        this.replaceSection(sections['cart-drawer'], '#CartDrawer');

        const mainCartSection = document.querySelector('#main-cart-items')?.closest('.shopify-section');
        this.replaceSection(sections['main-cart-items'], '.shopify-section', mainCartSection);
        this.replaceSection(sections['main-cart-footer'], '#main-cart-footer');

        const drawer = document.querySelector('cart-drawer');
        drawer?.classList.toggle('is-empty', !!drawer.querySelector('.drawer__inner-empty'));
        drawer?.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());

        window.renderUpsellProducts?.();
        window.initCarousel?.();
      }

      replaceSection(html, selector, currentElement = document.querySelector(selector)) {
        if (!html) return;
        const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
        const newElement = parsedHtml.querySelector(selector);
        if (currentElement && newElement) currentElement.innerHTML = newElement.innerHTML;
      }
    }
  );
}

if (!customElements.get('cart-reward-gift')) {
  customElements.define(
    'cart-reward-gift',
    class CartRewardGift extends customElements.get('free-gift-selector') {
      static running = false;
      static pending = null;

      connectedCallback() {
        this.syncGift();
      }

      async syncGift() {
        const state = this.constructor;
        if (state.running) {
          state.pending = this;
          return;
        }
        state.running = true;

        const root = window.Shopify?.routes?.root || '/';
        const request = async (path, body) => {
          const response = await fetch(`${root}${path}`, body ? {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          } : undefined);
          if (!response.ok) throw new Error('Could not update your free gift. Please try again.');
          return response.json();
        };

        try {
          const cart = await request('cart.js');
          const gifts = cart.items.filter((item) => item.properties?._cart_reward === '3');
          const total = cart.items.reduce((sum, item) =>
            sum - (item.properties?._cart_reward === '3' || item.properties?._free_gift === 'true'
              ? item.final_line_price : 0), cart.total_price);
          const variantId = Number(this.dataset.variantId);
          const threshold = Number(this.dataset.threshold);
          const eligible = variantId > 0 && threshold > 0 && total >= threshold;
          let keptGift = false;
          let changed = false;

          const giftThreshold = Number(this.dataset.giftThreshold);
          if (giftThreshold > 0 && total < giftThreshold) {
            for (const gift of cart.items.filter((item) => item.properties?._free_gift === 'true')) {
              await request('cart/change.js', { id: gift.key, quantity: 0 });
              changed = true;
            }
          }

          for (const gift of gifts) {
            const keep = eligible && gift.variant_id === variantId && !keptGift;
            const quantity = keep ? 1 : 0;
            if (gift.quantity !== quantity) {
              await request('cart/change.js', { id: gift.key, quantity });
              changed = true;
            }
            if (keep) keptGift = true;
          }

          if (eligible && !keptGift) {
            await request('cart/add.js', {
              items: [{ id: variantId, quantity: 1, properties: { _cart_reward: '3' } }],
            });
            changed = true;
          }

          if (changed) await this.refreshCart();
        } catch (error) {
          console.error('Cart reward gift:', error);
          document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors').forEach((element) => {
            element.textContent = error.message;
          });
        } finally {
          state.running = false;
          const pending = state.pending;
          state.pending = null;
          if (pending?.isConnected) pending.syncGift();
        }
      }
    }
  );
}
